import type { AbstractModel } from "@/scene/AbstractModel";
import { CleanerModel } from "@/scene/CleanerModel";
import { GLBModel } from "@/scene/GLBModel";
import type {
  AmbientFishConfig,
  EnvironmentDecorItemConfig,
  SceneProps,
} from "@/scene/SceneProps";
import { SkimmerModel } from "@/scene/SkimmerModel";
import { startBelugaScene } from "@/scene/startBelugaScene";
import { startCatfishScene } from "@/scene/startCatfishScene";
import { startNeonsScene } from "@/scene/startNeonsScene";
import { startStingrayScene } from "@/scene/startStingrayScene";
import { TubeBacteriesModel } from "@/scene/TubeBacteriesModel";
import { TubeModel } from "@/scene/TubeModel";
import type { UnderwaterVisualSettingsOverride } from "@/scene/VisualSettings";
import { WasteWater } from "@/scene/WasteWater";
import type { PollutionEffectConfig } from "@/scene/PollutionEffects";
import {
  BELUGA_ENVIRONMENT_DECOR,
  BELUGA_GARBAGE_MODELS,
  BLUETANG_ENVIRONMENT_DECOR,
  BLUETANG_GARBAGE_MODELS,
  ALL_SCENES_CAUSTICS_VISUAL_SETTINGS,
  CATFISH_ENVIRONMENT_DECOR,
  CATFISH_VISUAL_SETTINGS,
  DEFAULT_AMBIENT_FISH,
  FISH_1_AMBIENT_FISH,
  FISH_2_AMBIENT_FISH,
  FISH_2_AND_BLUETANG_AMBIENT_FISH,
  CARP_AND_CLOWNFISH_AMBIENT_FISH,
  FISH_2_AND_CLOWNFISH_AMBIENT_FISH,
  COPPERBAND_INDIVIDUAL_AMBIENT_FISH,
  INIYA_ENVIRONMENT_DECOR,
  INIYA_GARBAGE_MODELS,
  INIYA_VISUAL_SETTINGS,
  NEONS_AMBIENT_FISH,
  NEONS_ENVIRONMENT_DECOR,
  NEONS_GARBAGE_MODELS,
  OCTOPUS_ENVIRONMENT_DECOR,
  OCTOPUS_GARBAGE_MODELS,
  STINGRAY_ENVIRONMENT_DECOR,
  STINGRAY_GARBAGE_MODELS,
  STINGRAY_VISUAL_SETTINGS,
  TURTLE_ENVIRONMENT_DECOR,
  TURTLE_GARBAGE_MODELS,
  WHALE_ENVIRONMENT_DECOR,
  WHALE_GARBAGE_MODELS,
  BELUGA_VISUAL_SETTINGS,
  NEONS_VISUAL_SETTINGS,
  TURTLE_VISUAL_SETTINGS,
  BLUETANG_VISUAL_SETTINGS,
  OCTOPUS_VISUAL_SETTINGS,
  WHALE_VISUAL_SETTINGS,
} from "@/constants/scenes";

export { VISUAL_LAB_DECOR_MODELS } from "@/constants/scenes";

export type GameConfig = {
  id: string;
  weaponModel: GLBModel;
  garbageModels: AbstractModel[];
  environmentDecor?: EnvironmentDecorItemConfig[];
  pollutionEffect?: PollutionEffectConfig;
  visualSettings?: UnderwaterVisualSettingsOverride;
  ambientFish?: AmbientFishConfig | AmbientFishConfig[];
  totalGarbageCount: number;
  letter: { position: number; value: string };
  startScene: (props: SceneProps) => {
    onExit: () => void;
    onUpdate: () => void;
    onRender?: () => void;
  };
}

const SOLID_POLLUTION_EFFECT: PollutionEffectConfig = {
  kind: "suspended",
  intensity: 10.94,
  particleCount: 2_000,
  particleSizeScale: 1.36,
  particleOpacityScale: 1.45,
  motionSpeedScale: 0.2,
  horizontalDriftScale: 10.0,
  verticalDriftScale: 10.0,
  particleColor: "#799789",
};

const NEONS_SOLID_POLLUTION_EFFECT: PollutionEffectConfig = {
  kind: "suspended",
  intensity: 10.94,
  particleCount: 2_000,
  particleSizeScale: 1.36,
  particleOpacityScale: 1.45,
  motionSpeedScale: 0.2,
  horizontalDriftScale: 10.0,
  verticalDriftScale: 10.0,
  particleColor: "#8a9173",
};

const WHALE_SOLID_POLLUTION_EFFECT: PollutionEffectConfig = {
  kind: "suspended",
  intensity: 10.94,
  particleCount: 2_000,
  particleSizeScale: 1.36,
  particleOpacityScale: 1.45,
  motionSpeedScale: 0.2,
  horizontalDriftScale: 10.0,
  verticalDriftScale: 10.0,
  particleColor: "#596d83",
};
const CHEMICAL_POLLUTION_EFFECT: PollutionEffectConfig = {
  kind: "chemical",
  intensity: 6,
  opacityScale: 0.87,
};
const SOAP_POLLUTION_EFFECT: PollutionEffectConfig = {
  kind: "soap",
};
const TURTLE_GARBAGE_MAX_CAMERA_Y_OFFSET = 0.3;

export const GAMES: Record<string, GameConfig> = {
  stingray: {
    id: 'stingray',
    weaponModel: new GLBModel("/models/weapon/net_with_animation.glb"),
    garbageModels: STINGRAY_GARBAGE_MODELS,
    environmentDecor: STINGRAY_ENVIRONMENT_DECOR,
    pollutionEffect: SOLID_POLLUTION_EFFECT,
    visualSettings: STINGRAY_VISUAL_SETTINGS,
    ambientFish: FISH_2_AND_BLUETANG_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 0, value: 'Э' },
    startScene: startStingrayScene,
  },
  catfish: {
    id: 'catfish',
    weaponModel: new TubeBacteriesModel(),
    garbageModels: [new WasteWater(18)],
    environmentDecor: CATFISH_ENVIRONMENT_DECOR,
    pollutionEffect: NEONS_SOLID_POLLUTION_EFFECT,
    visualSettings: CATFISH_VISUAL_SETTINGS,
    ambientFish: FISH_1_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 2, value: 'О' },
    startScene: startCatfishScene,
  },
  neons: {
    id: 'neons',
    weaponModel: new CleanerModel(),
    garbageModels: NEONS_GARBAGE_MODELS,
    environmentDecor: NEONS_ENVIRONMENT_DECOR,
    pollutionEffect: NEONS_SOLID_POLLUTION_EFFECT,
    visualSettings: NEONS_VISUAL_SETTINGS,
    ambientFish: [NEONS_AMBIENT_FISH, COPPERBAND_INDIVIDUAL_AMBIENT_FISH],
    totalGarbageCount: 21,
    letter: { position: 5, value: 'Г' },
    startScene: startNeonsScene,
  },
  beluga: {
    id: 'beluga',
    weaponModel: new SkimmerModel(),
    garbageModels: BELUGA_GARBAGE_MODELS,
    environmentDecor: BELUGA_ENVIRONMENT_DECOR,
    pollutionEffect: CHEMICAL_POLLUTION_EFFECT,
    visualSettings: BELUGA_VISUAL_SETTINGS,
    ambientFish: DEFAULT_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 1, value: 'К' },
    startScene: startBelugaScene,
  },
  iniya: {
    id: 'iniya',
    weaponModel: new TubeModel(),
    garbageModels: INIYA_GARBAGE_MODELS,
    environmentDecor: INIYA_ENVIRONMENT_DECOR,
    pollutionEffect: SOAP_POLLUTION_EFFECT,
    visualSettings: INIYA_VISUAL_SETTINGS,
    ambientFish: CARP_AND_CLOWNFISH_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 3, value: 'Л' },
    startScene: startCatfishScene,
  },
  blueTang: {
    id: 'blueTang',
    weaponModel: new CleanerModel(),
    garbageModels: BLUETANG_GARBAGE_MODELS,
    environmentDecor: BLUETANG_ENVIRONMENT_DECOR,
    pollutionEffect: NEONS_SOLID_POLLUTION_EFFECT,
    visualSettings: BLUETANG_VISUAL_SETTINGS,
    ambientFish: NEONS_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 6, value: 'И' },
    startScene: startNeonsScene,
  },
  octopus: {
    id: 'octopus',
    weaponModel: new CleanerModel(),
    garbageModels: OCTOPUS_GARBAGE_MODELS,
    environmentDecor: OCTOPUS_ENVIRONMENT_DECOR,
    pollutionEffect: NEONS_SOLID_POLLUTION_EFFECT,
    visualSettings: OCTOPUS_VISUAL_SETTINGS,
    ambientFish: [NEONS_AMBIENT_FISH, COPPERBAND_INDIVIDUAL_AMBIENT_FISH],
    totalGarbageCount: 21,
    letter: { position: 7, value: 'К' },
    startScene: startNeonsScene,
  },
  turtle: {
    id: 'turtle',
    weaponModel: new GLBModel("/models/weapon/net_with_animation.glb"),
    garbageModels: TURTLE_GARBAGE_MODELS,
    environmentDecor: TURTLE_ENVIRONMENT_DECOR,
    pollutionEffect: SOLID_POLLUTION_EFFECT,
    visualSettings: TURTLE_VISUAL_SETTINGS,
    ambientFish: NEONS_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 4, value: 'О' },
    startScene: (props) =>
      startStingrayScene({
        ...props,
        garbageSpawnConfig: {
          ...props.garbageSpawnConfig,
          maxCameraYOffset: TURTLE_GARBAGE_MAX_CAMERA_Y_OFFSET,
        },
      }),
  },
  whale: {
    id: 'whale',
    weaponModel: new GLBModel("/models/weapon/net_with_animation.glb"),
    garbageModels: WHALE_GARBAGE_MODELS,
    environmentDecor: WHALE_ENVIRONMENT_DECOR,
    pollutionEffect: WHALE_SOLID_POLLUTION_EFFECT,
    visualSettings: WHALE_VISUAL_SETTINGS,
    ambientFish: FISH_2_AND_CLOWNFISH_AMBIENT_FISH,
    totalGarbageCount: 21,
    letter: { position: 8, value: 'А' },
    startScene: (props) => startStingrayScene({ ...props, garbageSpawnConfig: { minRadius: 2.2, maxRadius: 3.5 } }),
  },
} as const;

export type GameId = keyof typeof GAMES;
